import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useT } from '../i18n/i18n';
import { Chip, DropSpot, usePlacer } from '../games/kit/place';
import Celebration from './Celebration';
import { WORD_LISTS, cleanWords, loadDictionary } from './words';
import { RACK_SIZE, check, findPlan, refill, type Grid, type Placed } from './wordTilesRules';

export type WordTilesOptions = { source: 'random' | 'list' | 'custom'; list: string; custom: string; size: number; target: number; motion: boolean };

export const WORD_TILES_DEFAULTS: WordTilesOptions = { source: 'random', list: 'animals', custom: '', size: 7, target: 5, motion: true };

export const TARGETS: Record<number, number[]> = { 5: [3, 4], 7: [3, 5, 8], 9: [3, 5, 8, 10] };

const fitTarget = (size: number, target: number) => { const ok = TARGETS[size] ?? [3]; return ok.includes(target) ? target : Math.max(...ok.filter((k) => k <= target), ok[0]); };

export function wordsFor(o: WordTilesOptions): string[] {
  if (o.source === 'custom') return cleanWords(o.custom, o.size);
  if (o.source === 'list') return WORD_LISTS[o.list]?.words ?? WORD_LISTS.animals.words;
  return WORD_LISTS.everyday.words;
}

export function WordTilesOptionsForm({ value, onChange }: { value: WordTilesOptions; onChange: (v: WordTilesOptions) => void }) {
  const t = useT();
  const custom = cleanWords(value.custom, value.size);
  return (
    <>
      <fieldset className="sub-group">
        <legend className="field-label">{t('Letters come from')}</legend>
        {([['random', t('Random everyday words')], ['list', t('A word list')], ['custom', t('My own words')]] as const).map(([v, label]) => (
          <label key={v} className="check">
            <input type="radio" name="wt-source" checked={value.source === v} onChange={() => onChange({ ...value, source: v })} />
            {label}
          </label>
        ))}
      </fieldset>
      {value.source === 'list' && (
        <div className="field">
          <label htmlFor="wt-list">{t('Word list')}</label>
          <select id="wt-list" value={value.list} onChange={(e) => onChange({ ...value, list: e.target.value })}>
            {Object.entries(WORD_LISTS).filter(([k]) => k !== 'everyday').map(([k, l]) => <option key={k} value={k}>{t(l.label)}</option>)}
          </select>
        </div>
      )}
      {value.source === 'custom' && (
        <div className="field">
          <label htmlFor="wt-custom">{t('Your words')}</label>
          <textarea id="wt-custom" rows={3} value={value.custom} placeholder={t('e.g. cat, sun, apple')} onChange={(e) => onChange({ ...value, custom: e.target.value })} />
          <p className="field-hint">{t('Separate words with commas or spaces. Letters only, up to {n} letters each. {count} words ready.', { n: value.size, count: custom.length })}</p>
        </div>
      )}
      <div className="field">
        <label htmlFor="wt-size">{t('Board size')}</label>
        <select id="wt-size" value={value.size} onChange={(e) => { const size = Number(e.target.value); onChange({ ...value, size, target: fitTarget(size, value.target) }); }}>
          <option value={5}>{t('5 by 5')}</option>
          <option value={7}>{t('7 by 7')}</option>
          <option value={9}>{t('9 by 9')}</option>
        </select>
      </div>
      <div className="field">
        <label htmlFor="wt-target">{t('Word target')}</label>
        <select id="wt-target" value={value.target} onChange={(e) => onChange({ ...value, target: Number(e.target.value) })}>
          {(TARGETS[value.size] ?? [3]).map((k) => <option key={k} value={k}>{t('{n} words', { n: k })}</option>)}
        </select>
      </div>
      <label className="check">
        <input type="checkbox" checked={value.motion} onChange={(e) => onChange({ ...value, motion: e.target.checked })} />
        {t('Moving pieces: letters drop in')}
      </label>
    </>
  );
}

const ALL_WORDS = [...new Set(Object.values(WORD_LISTS).flatMap((l) => l.words))];

type Tile = { id: string; letter: string };

let tileSeq = 0;
const tiles = (letters: string[]): Tile[] => letters.map((letter) => ({ id: `t${++tileSeq}`, letter }));

export default function WordTiles({ options, onAgain }: { options: WordTilesOptions; onAgain: () => void }) {
  const t = useT();
  const n = options.size;
  const list = useMemo(() => wordsFor(options), [options]);
  const listSet = useMemo(() => new Set(list), [list]);
  const [dict, setDict] = useState<Set<string> | null>(null);
  const [failed, setFailed] = useState(false);
  const [grid, setGrid] = useState<Grid>(() => new Array(n * n).fill(null));
  const [placed, setPlaced] = useState<Map<number, Tile>>(new Map());
  const [rack, setRack] = useState<Tile[]>([]);
  const [made, setMade] = useState<string[]>([]);
  const [message, setMessage] = useState<string | null>(null);
  const [finished, setFinished] = useState(false);
  const won = made.length >= options.target;
  const started = useRef(false);
  const faces = useRef<(HTMLSpanElement | null)[]>([]);
  const [dropAt, setDropAt] = useState<{ seq: number; cell: number } | null>(null);

  useLayoutEffect(() => {
    if (!dropAt || !options.motion) return;
    faces.current[dropAt.cell]?.animate(
      [{ transform: 'translateY(-28px) scale(1.2)', opacity: 0 }, { transform: 'translateY(3px) scale(0.96)', opacity: 1, offset: 0.75 }, { transform: 'none', opacity: 1 }],
      { duration: 300, easing: 'ease-in' },
    );
  }, [dropAt, options.motion]);


  const valid = useCallback((w: string) => listSet.has(w) || !!dict?.has(w), [dict, listSet]);

  const deal = useCallback((g: Grid, keep: string[]) => {
    const plan = findPlan(g, n, list, valid) ?? findPlan(g, n, ALL_WORDS, valid);
    if (!plan && !g.every((c) => c === null)) { setFinished(true); return; }
    setRack(tiles(refill(keep, plan)));
  }, [list, n, valid]);

  useEffect(() => {
    let live = true;
    loadDictionary().then((d) => { if (live) setDict(d); }).catch(() => { if (live) setFailed(true); });
    return () => { live = false; };
  }, []);

  useEffect(() => {
    if (started.current || (!dict && !failed) || list.length === 0) return;
    started.current = true;
    deal(new Array(n * n).fill(null), []);
  }, [dict, failed, list, deal, n]);

  const placer = usePlacer((tileId, target) => {
    const cell = Number(target.slice(5));
    if (grid[cell] !== null || placed.has(cell)) return;
    const tile = rack.find((x) => x.id === tileId);
    if (!tile) return;
    setRack((r) => r.filter((x) => x.id !== tileId));
    setPlaced((p) => new Map(p).set(cell, tile));
    setDropAt((d) => ({ seq: (d?.seq ?? 0) + 1, cell }));
    setMessage(null);
  });

  function takeBack(cell?: number) {
    const back = cell === undefined ? [...placed.values()] : [placed.get(cell)!].filter(Boolean);
    setRack((r) => [...r, ...back]);
    setPlaced((p) => { const next = new Map(p); if (cell === undefined) next.clear(); else next.delete(cell); return next; });
    setMessage(null);
  }

  function submit() {
    const letters: Placed = new Map([...placed].map(([i, tile]) => [i, tile.letter]));
    const v = check(grid, letters, n, valid);
    if (!v.ok) {
      const why: Record<string, string> = {
        empty: t('Put some letters on the board first.'),
        line: t('Put the letters in one straight line, across or down.'),
        gap: t('Fill the gap so the letters make one word.'),
        short: t('A word needs at least two letters.'),
        join: t('Join your word to letters already on the board.'),
        word: t('"{word}" is not in the dictionary. Try another word.', { word: v.word ?? '' }),
      };
      setMessage(why[v.reason]);
      return;
    }
    const next = [...grid];
    letters.forEach((l, i) => { next[i] = l; });
    setGrid(next);
    setPlaced(new Map());
    setMade((m) => [...m, v.words[0]]);
    setMessage(t('"{word}" is a word.', { word: v.words[0] }));
    if (made.length + 1 >= options.target) setFinished(true);
    else deal(next, rack.map((x) => x.letter));
  }

  function newLetters() {
    takeBack();
    deal(grid, []);
  }

  if (list.length === 0) return <p className="error" role="alert">{t('Add at least one word of 2 or more letters to play.')}</p>;
  if (!dict && !failed) return <p className="muted" role="status">{t('Getting the dictionary ready…')}</p>;

  return (
    <div className="lz-words">
      {failed && <p className="field-hint" role="status">{t('The dictionary could not be loaded, so only words from your list count.')}</p>}
      <p className="lz-status" aria-live="polite">
        {won ? t('You reached your target: {n} words!', { n: made.length }) : finished ? t('No more words fit on the board. You made {n} of {target} words.', { n: made.length, target: options.target }) : t('Make a word with your letters. Words made: {n} of {target}', { n: made.length, target: options.target })}
      </p>
      {message && <p className="lz-message" role="status">{message}</p>}
      <div className="lz-wt-stage">
      {won && <Celebration text={t('You did it — {n} words!', { n: made.length })} motion={options.motion} />}
      <div className="lz-wt-board" style={{ gridTemplateColumns: `repeat(${n}, 1fr)` }}>
        {grid.map((letter, i) => {
          const face = (content: string | null, cls: string) => <span ref={(el) => { faces.current[i] = el; }} className={`lz-wt-face ${cls}`}>{content && <span className="lz-wt-letter">{content}</span>}</span>;
          if (letter) return <span key={i} className="lz-wt-cell is-fixed">{face(letter.toUpperCase(), 'is-letter')}</span>;
          const mine = placed.get(i);
          if (mine) return <button key={i} type="button" className="lz-wt-cell is-placed" aria-label={t('Take back {letter}', { letter: mine.letter.toUpperCase() })} onClick={() => takeBack(i)}>{face(mine.letter.toUpperCase(), 'is-letter is-new')}</button>;
          return <DropSpot key={i} placer={placer} id={`cell-${i}`} label={t('Square {n}', { n: i + 1 })} className="lz-wt-cell lz-wt-empty">{face(null, 'is-empty')}</DropSpot>;
        })}
      </div>
      </div>
      {!finished && (
        <>
          <div className="lz-rack" role="group" aria-label={t('Your letters')}>
            {rack.map((tile) => (
              <Chip key={tile.id} placer={placer} id={tile.id} label={tile.letter.toUpperCase()} className="lz-tile">
                <span className="lz-wt-letter">{tile.letter.toUpperCase()}</span>
              </Chip>
            ))}
            {rack.length < RACK_SIZE && Array.from({ length: RACK_SIZE - rack.length }, (_, i) => <span key={`gap-${i}`} className="lz-tile-gap" aria-hidden="true" />)}
          </div>
          <div className="toolbar lz-actions">
            <button type="button" className="btn" disabled={placed.size === 0} onClick={submit}>{t('Check my word')}</button>
            <button type="button" className="btn secondary" disabled={placed.size === 0} onClick={() => takeBack()}>{t('Take letters back')}</button>
            <button type="button" className="btn ghost" onClick={newLetters}>{t('New letters')}</button>
          </div>
        </>
      )}
      {finished && <div className="toolbar"><button type="button" className="btn" onClick={onAgain}>{t('Play again')}</button></div>}
      {made.length > 0 && (
        <p className="field-hint">{t('Your words: {list}', { list: made.join(', ') })}</p>
      )}
    </div>
  );
}
